/**
 * Debriefing Guide Data Retrieval Functions
 *
 * This module handles fetching and preparing session data for
 * ORCA360 CBTA Debriefing Guide export (Part A and beyond)
 */

import { supabase } from './supabase';
import type {
  SessionDataForExport,
  DebriefingPartAData
} from '../types/debriefing-export';

/**
 * Retrieve complete session data for debriefing guide export
 * Fetches session with all related data (users, session types, organization)
 */
export async function retrieveSessionDataForExport(sessionId: string): Promise<SessionDataForExport | null> {
  try {
    // Fetch session with all related data via joins
    const { data: session, error } = await supabase
      .from('sessions')
      .select(`
        *,
        trainee:trainee_id (
          id,
          full_name,
          licence_id
        ),
        instructor:instructor_id (
          id,
          full_name,
          licence_id
        ),
        session_type:session_type_id (
          code,
          name
        ),
        organization:organization_id (
          id,
          name,
          code
        )
      `)
      .eq('id', sessionId)
      .single();

    if (error) {
      console.error('Error fetching session for export:', error);
      return null;
    }

    if (!session) {
      console.error('Session not found:', sessionId);
      return null;
    }

    // Check if there's an existing debriefing guide
    const { data: debriefingGuide } = await supabase
      .from('debriefing_guides')
      .select('*')
      .eq('session_id', sessionId)
      .single();

    // Format the data to match SessionDataForExport interface
    const exportData: SessionDataForExport = {
      // Core session fields
      id: session.id,
      session_date: session.session_date,
      aircraft_type: session.aircraft_type,
      simulator_id: session.simulator_id,
      scenario_code: session.scenario_code,
      flight_phase: session.flight_phase,
      session_notes: session.session_notes,

      // New Part A fields from Migration 074
      session_start_time: session.session_start_time,
      session_end_time: session.session_end_time,
      time_pf_minutes: session.time_pf_minutes,
      time_pm_minutes: session.time_pm_minutes,
      location: session.location,
      overall_result: session.overall_result,
      pilot_function: session.pilot_function,

      // Related entities (from joins)
      trainee: session.trainee ? {
        id: session.trainee.id,
        full_name: session.trainee.full_name || 'Unknown',
        licence_id: session.trainee.licence_id
      } : undefined,

      instructor: session.instructor ? {
        id: session.instructor.id,
        full_name: session.instructor.full_name || 'Unknown',
        licence_id: session.instructor.licence_id
      } : undefined,

      session_type: session.session_type ? {
        code: session.session_type.code,
        name: session.session_type.name
      } : undefined,

      organization: session.organization ? {
        id: session.organization.id,
        name: session.organization.name,
        code: session.organization.code
      } : undefined,

      // Existing debriefing guide data if available
      debriefing_guide: debriefingGuide ? {
        id: debriefingGuide.id,
        part_a: debriefingGuide.part_a,
        part_b: debriefingGuide.part_b,
        part_c: debriefingGuide.part_c,
        part_d: debriefingGuide.part_d,
        status: debriefingGuide.status || 'draft'
      } : undefined
    };

    return exportData;
  } catch (err) {
    console.error('Error in retrieveSessionDataForExport:', err);
    return null;
  }
}

/**
 * Get Part A data for a session
 * This is the primary function for preparing Part A export data
 */
export async function getPartADataForSession(sessionId: string): Promise<{
  data: DebriefingPartAData | null;
  validation: { isValid: boolean; missingFields: string[] };
  sessionData: SessionDataForExport | null;
}> {
  // Import the mapping function dynamically to avoid circular dependencies
  const { mapSessionToPartA, validatePartAData } = await import('../types/debriefing-export');

  // Retrieve full session data
  const sessionData = await retrieveSessionDataForExport(sessionId);

  if (!sessionData) {
    return {
      data: null,
      validation: { isValid: false, missingFields: ['Session not found'] },
      sessionData: null
    };
  }

  // Map to Part A structure
  const partAData = mapSessionToPartA(sessionData);

  // Validate completeness
  const validation = validatePartAData(partAData);

  return {
    data: partAData,
    validation,
    sessionData
  };
}

/**
 * Save or update Part A data in the debriefing_guides table
 */
export async function savePartAData(
  sessionId: string,
  partAData: DebriefingPartAData
): Promise<{ success: boolean; error?: string }> {
  try {
    // Check if debriefing guide exists
    const { data: existing } = await supabase
      .from('debriefing_guides')
      .select('id')
      .eq('session_id', sessionId)
      .single();

    const partAJson = JSON.stringify(partAData);

    if (existing) {
      // Update existing record
      const { error } = await supabase
        .from('debriefing_guides')
        .update({
          part_a: partAJson,
          updated_at: new Date().toISOString()
        })
        .eq('session_id', sessionId);

      if (error) throw error;
    } else {
      // Create new record
      const { error } = await supabase
        .from('debriefing_guides')
        .insert({
          session_id: sessionId,
          part_a: partAJson,
          status: 'draft',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
    }

    return { success: true };
  } catch (err) {
    console.error('Error saving Part A data:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error'
    };
  }
}

/**
 * Check if all required Part A fields are filled for a session
 */
export async function checkPartACompleteness(sessionId: string): Promise<{
  isComplete: boolean;
  missingFields: string[];
  completionPercentage: number;
}> {
  const { data, validation } = await getPartADataForSession(sessionId);

  if (!data) {
    return {
      isComplete: false,
      missingFields: ['Session data not found'],
      completionPercentage: 0
    };
  }

  // Calculate completion percentage
  const requiredFields = [
    'trainee.name', 'trainee.id',
    'instructor.name', 'instructor.id',
    'aircraftType', 'sessionDate',
    'startTime', 'endTime',
    'timeAsPF', 'timeAsPM',
    'location', 'simulatorId',
    'overallResult', 'pilotFunction'
  ];

  const filledFields = requiredFields.filter(field => {
    const value = field.split('.').reduce((obj, key) => obj?.[key], data as any);
    return value && value !== 'Not specified' && value !== 'Not recorded' && value !== 'N/A';
  });

  const completionPercentage = Math.round((filledFields.length / requiredFields.length) * 100);

  return {
    isComplete: validation.isValid,
    missingFields: validation.missingFields,
    completionPercentage
  };
}

/**
 * Get a summary of Part A data for display purposes
 */
export function getPartASummary(partAData: DebriefingPartAData): string {
  const lines = [
    `Session Date: ${partAData.sessionDate}`,
    `Aircraft: ${partAData.aircraftType}`,
    `Trainee: ${partAData.trainee.name}`,
    `Instructor: ${partAData.instructor.name}`,
    `Duration: ${partAData.startTime} - ${partAData.endTime}`,
    `Result: ${partAData.overallResult}`,
  ];

  return lines.join('\n');
}